<template>
  <div class="flex flex-col w-full items-start">
    <!-- LABEL -->
    <p
      v-if="inputLabel"
      class="flex items-center font-semibold text-gray-500 dark:text-gray-400 mb-2 text-left capitalize"
      :class="[uppercaseLabel && 'uppercase', `text-${size}`]"
    >
      {{ inputLabel }} <span v-if="required" class="text-red-500">*</span>
      <FloatingTooltip v-if="tooltip || $slots.tooltip" placement="top" class="ml-2">
        <template #button>
          <font-awesome-icon
            icon="fa-solid fa-circle-question"
            class="text-primary dark:text-primary-200 dark:hover:text-primary-100 hover:text-primary-500 dark:hover:shadow-primarydark/30 cursor-pointer transition"
          />
        </template>

        <template #tooltip>
          <!-- Prefer slot, fallback to string -->
          <slot name="tooltip">
            {{ tooltip }}
          </slot>
        </template>
      </FloatingTooltip>
    </p>
    <!-- DROPDOWN -->
    <div class="w-full relative flex flex-grow items-stretch" :class="!valid && 'border rounded border-red-500'">
      <VDropdownSelector
        :options="options"
        :placeholder="placeholder"
        :fields="fields"
        :value="selOption"
        :disabled="disabled"
        :searchable="searchable"
        :search-placeholder="searchPlaceholder"
        :height="height"
        :has-attached-btn="!!attachedBtn"
        @select="changeVal"
        ref="dropdownSelector"
      >
        <template #option="{ option }">
          <slot name="option" :option="option"></slot>
        </template>
      </VDropdownSelector>
      <!-- ATTACHED BUTTON -->
      <button
        v-if="attachedBtn"
        @click="emitClick"
        type="button"
        :disabled="attachedBtn.disabled"
        class="relative -ml-px inline-flex items-center space-x-2 rounded-r-md
        border border-primary px-4 text-sm font-semibold
        focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        :class="attachedBtn.disabled
          ? 'text-white bg-gray-400 cursor-not-allowed'
          : 'bg-primary text-gray-700 dark:text-gray-300 hover:bg-primary-400 focus:bg-primary-400'
        "
      >
        <font-awesome-icon v-if="attachedBtn.icon" :icon="attachedBtn.icon" class="h-5 w-5 text-white" />
        <span class="text-white">
          {{ attachedBtn.label }}
        </span>
      </button>
    </div>
    <p v-if="!valid" class="text-red-700 font-medium text-sm text-left mt-3">
      {{ validationMessage }}
    </p>
  </div>
</template>

<script>
import FloatingTooltip from "@/components/Tooltips/FloatingTooltip.vue"
import VDropdownSelector from "./VDropdownSelector.vue"
import {
  hasLeftDropdownAppend,
  hasDropdownAppendContent,
} from "./dropdownAppend.js"

export default {
  name: "VDropdown",
  components: {
    FloatingTooltip,
    VDropdownSelector,
  },
  props: {
    options: {
      type: Array,
      required: true
    },
    selOption: {
      type: Object,
      required: false
    },
    inputLabel: {
      type: String,
      required: false
    },
    uppercaseLabel: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      required: false
    },
    fields: {
      type: Object,
      default: () => ({ text: "name", value: "id" }),
    },
    placeholder: {
      type: String,
      required: false
    },
    tooltip: {
      type: String,
      required: false
    },
    valid: {
      type: Boolean,
      required: false,
      default: true
    },
    validationMessage: {
      type: String,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    objectIndex: {
      type: Number,
      default: 0
    },
    attachedBtn: {
      type: Object,
      required: false
    },
    size: {
      type: String,
      default: "md"
    },
    height: {
      type: String,
      default: "h-10"
    },
    searchable: {
      type: Boolean,
      default: true
    },
    searchPlaceholder: {
      type: String,
      required: false
    }
  },
  emits: ["btnClick", "select", "closeDropdown"],
  data() {
    return {
      selValue: this.selOption ? this.selOption[this.fields.value] : null,
      highlightedValue: null,
      filterPlaceholder: "Search",
    }
  },
  computed: {
    optionsLength() {
      return this.options.length
    }
  },
  watch: {
    selOption(val, oldVal) {
      if (val !== oldVal) {
        if (val !== null) this.selValue = this.selOption[this.fields.value]
        else this.selValue = val
      }
    },
    options: {
      handler(val, oldVal) {
        if (val !== oldVal) this.appendAllLeft()
      },
      deep: true
    }
  },
  mounted() {
    this.appendAllLeft()
  },
  methods: {
    changeVal(event) {
      this.highlightedValue = event.itemData
      this.handleClose()
    },
    emitClick() {
      this.$emit("btnClick", this.fields.text)
    },
    async handleClose() {
      if (this.highlightedValue) this.$emit("select", this.highlightedValue)
      this.$emit("closeDropdown")
    },
    appendAllLeft() {
      // if one of the options has a component positioned to the left,
      // all options should also be positioned to the left (but not adding a component)
      if (this.options.find(x => hasLeftDropdownAppend(x.dropdownAppend))) {
        this.options.filter(x => !x.dropdownAppend).forEach(x => {
          x.dropdownAppend = { position: "left" }
        })
      }
      // if no options with append content remain, remove left spacers
      else if (!this.options.find(x => hasDropdownAppendContent(x.dropdownAppend))) {
        this.options.filter(x => x.dropdownAppend?.position === "left").forEach(x => {
          x.dropdownAppend.position = null
        })
      }
    }
  }
}

// POTENTIAL BUGS AND TROUBLESHOOTING
/*

- If new dropdown item is created using attachedBtn modal call, make sure to await the fresh dropdown list before assigning the new one to selOption
*/
</script>

<style scoped>
.no-right-border{
  border-top-right-radius: 0px !important;
  border-bottom-right-radius: 0px !important;
}
/* ── VButton frosted glass (dark mode) ─────────── */
/* Primary filled */
[data-theme="dark"] .bg-primary {
  background: rgba(0, 146, 191, 0.30) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 188, 245, 0.30) !important;
  box-shadow: 0 2px 8px rgba(0, 146, 191, 0.25);
}

[data-theme="dark"] .bg-primary:hover,
[data-theme="dark"] .hover\:bg-primary-400:hover {
  background: rgba(0, 146, 191, 0.5) !important;
  border-color: rgba(0, 188, 245, 0.75) !important;
  box-shadow: 0 4px 20px rgba(0, 146, 191, 0.15);
}
</style>